/**
 * Push Notifications Hook — Web (Firebase) + Native (Capacitor)
 * Requests permission, registers FCM token with backend,
 * handles notification tap routing on native,
 * and continuously monitors that permission stays granted.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { platform } from '@/utils/platform';
import { http } from '@/network/http';
import { useAuthStore } from '@/features/auth';

const FCM_TOKEN_KEY = 'fcm_push_token';
const PERMISSION_CHECK_INTERVAL = 30_000; // 30 seconds

export function usePushNotifications() {
  const navigate = useNavigate();
  const initialized = useRef(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // True when notifications are supported but NOT granted.
  // This drives the mandatory "re-enable" banner in AppLayout.
  const [pushBlocked, setPushBlocked] = useState(false);

  /** Check current permission state without requesting (web + native). */
  const checkPushGranted = useCallback(async (): Promise<boolean> => {
    try {
      if (platform.isWeb) {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
          return true; // not supported — don't block the app
        }
        return Notification.permission === 'granted';
      }
      if (platform.isNative) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const result = await PushNotifications.checkPermissions();
        return result.receive === 'granted';
      }
      return true; // unknown platform — don't block
    } catch {
      return true; // error checking — don't block
    }
  }, []);

  /** Sync pushBlocked state from live permission. */
  const refreshPermissionState = useCallback(async () => {
    const granted = await checkPushGranted();
    setPushBlocked(!granted);
  }, [checkPushGranted]);

  // ── Init: register token if already granted, start monitoring ──

  useEffect(() => {
    if (!isAuthenticated || initialized.current) return;
    initialized.current = true;

    if (platform.isWeb) {
      if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

      if (Notification.permission === 'granted') {
        registerWebPushToken();
        setPushBlocked(false);
      } else {
        setPushBlocked(true);
      }
    } else if (platform.isNative) {
      initNativePush();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Periodic + visibility-change monitoring ──

  useEffect(() => {
    if (!isAuthenticated) return;

    // Re-check when user returns to the tab/app (may have toggled in settings)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshPermissionState();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Periodic check
    const interval = setInterval(refreshPermissionState, PERMISSION_CHECK_INTERVAL);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [isAuthenticated, refreshPermissionState]);

  /** User tapped "Enable" — request permission via user gesture, then register.
   *  Returns true only if permission was actually granted. */
  async function enableNotifications(): Promise<boolean> {
    try {
      if (platform.isNative) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive === 'granted') {
          await PushNotifications.register();
          setPushBlocked(false);
          return true;
        }
        return false;
      }
      // Web
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        await registerWebPushToken();
        setPushBlocked(false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async function registerToken(token: string, platformName: string) {
    try {
      localStorage.setItem(FCM_TOKEN_KEY, token);
      await http.post('/push/register', { token, platform: platformName });
      console.log(`[push] Token registered (${platformName})`);
    } catch (error) {
      console.error('[push] Failed to register token:', error);
    }
  }

  async function registerWebPushToken() {
    try {
      const { messaging, VAPID_KEY } = await import('@/firebase/config');
      if (!messaging || !VAPID_KEY) return;

      const { getToken, onMessage } = await import('firebase/messaging');
      const swRegistration = await navigator.serviceWorker.ready;

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });

      if (token) {
        await registerToken(token, 'web');
      }

      // Show notification even when the app tab is in the foreground.
      // FCM delivers foreground messages to onMessage (not the SW push event).
      onMessage(messaging, (payload) => {
        const notif = payload.notification || {};
        const data = payload.data || {};
        swRegistration.showNotification(notif.title || document.title, {
          body: notif.body || '',
          icon: notif.icon || '/icons/icon-192.png',
          badge: '/icons/icon-192.png',
          tag: data.type || 'default',
          renotify: true,            // re-alert even if same tag
          requireInteraction: true,  // stay visible until dismissed
          silent: false,
          vibrate: [200, 100, 200],
          data,
        } as NotificationOptions);
      });
    } catch (error) {
      console.error('[push] Web push init error:', error);
    }
  }

  async function initNativePush() {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');

      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive !== 'granted') {
        setPushBlocked(true);
        return;
      }

      setPushBlocked(false);
      await PushNotifications.register();

      // Create high-importance channel so notifications show as
      // heads-up popups, on the lock screen, and with sound/vibration.
      // The channel_id MUST match what the backend sends in the FCM payload.
      if (platform.isAndroid) {
        await PushNotifications.createChannel({
          id: 'brainsway_default',
          name: 'Notifications',
          description: 'Messages, approvals, and alerts',
          importance: 4, // IMPORTANCE_HIGH — heads-up popups + sound
          visibility: 1,  // VISIBILITY_PUBLIC — show on lock screen
          sound: 'default',
          vibration: true,
          lights: true,
        });
      }

      PushNotifications.addListener('registration', async (token) => {
        const platformName = platform.isIOS ? 'ios' : 'android';
        await registerToken(token.value, platformName);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('[push] Native registration error:', error);
      });

      // Handle notification tap — route to the correct page
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const data = action.notification?.data || {};
        switch (data.type) {
          case 'NEW_MESSAGE':
            navigate(data.conversationId ? `/messages/${data.conversationId}` : '/messages');
            break;
          case 'REPORT_APPROVED':
          case 'REPORT_REJECTED':
            navigate('/notifications');
            break;
          case 'REPORT_SUBMITTED':
            navigate('/admin/report-approval');
            break;
          case 'POST_TAGGED':
            navigate('/feed');
            break;
          case 'NEW_USER_PENDING':
            navigate('/admin/user-approvals');
            break;
          default:
            navigate('/notifications');
        }
      });
    } catch (error) {
      console.error('[push] Native push init error:', error);
    }
  }

  return { pushBlocked, enableNotifications, checkPushGranted };
}
